<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import StatusChip from '../agendamentos/components/StatusChip.vue';

const store = useStore();
const { t } = useI18n();

const STATUS_KEYS = ['pending', 'confirmed', 'cancelled'];
const COLORS = ['teal', 'ruby', 'amber', 'slate', 'blue'];

const labels = computed(() => store.getters['painel/getStatusLabels']);
const colors = computed(() => store.getters['painel/getStatusColors']);

const localLabels = ref({ ...labels.value });
const localColors = ref({ ...colors.value });

watch(labels, v => {
  localLabels.value = { ...v };
});
watch(colors, v => {
  localColors.value = { ...v };
});

const businessHours = computed(
  () => store.getters['painel/getPainelSettings']?.business_hours
);

const save = async () => {
  await store.dispatch('painel/updateSettings', {
    business_hours: businessHours.value || { start: '08:00', end: '20:00' },
    status_labels: localLabels.value,
    status_colors: localColors.value,
  });
};

const resetDefaults = () => {
  localLabels.value = {
    pending: 'Pendente',
    confirmed: 'Confirmado',
    cancelled: 'Cancelado',
  };
  localColors.value = {
    pending: 'amber',
    confirmed: 'teal',
    cancelled: 'ruby',
  };
};
</script>

<template>
  <div class="space-y-4">
    <p class="text-sm text-n-slate-11">
      {{ t('PAINEL.STATUS_CUSTOMIZATION.PAGE_SUBTITLE') }}
    </p>

    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="text-left text-xs uppercase text-n-slate-10">
            <th class="py-2">
              {{ t('PAINEL.STATUS_CUSTOMIZATION.TABLE_KEY') }}
            </th>
            <th class="py-2">
              {{ t('PAINEL.STATUS_CUSTOMIZATION.TABLE_LABEL') }}
            </th>
            <th class="py-2">
              {{ t('PAINEL.STATUS_CUSTOMIZATION.TABLE_COLOR') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="key in STATUS_KEYS"
            :key="key"
            class="border-t border-n-strong"
          >
            <td class="py-2 font-mono text-xs text-n-slate-11">{{ key }}</td>
            <td class="py-2">
              <input
                v-model="localLabels[key]"
                type="text"
                class="w-48 rounded border border-n-strong bg-n-surface-1 px-2 py-1 text-sm"
              />
            </td>
            <td class="py-2">
              <div class="flex items-center gap-3">
                <select
                  v-model="localColors[key]"
                  class="rounded border border-n-strong bg-n-surface-1 px-2 py-1 text-sm"
                >
                  <option v-for="c in COLORS" :key="c" :value="c">
                    {{ c }}
                  </option>
                </select>
                <StatusChip :status="key" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button
        class="rounded bg-n-slate-3 px-4 py-2 text-sm"
        @click="resetDefaults"
      >
        {{ t('PAINEL.STATUS_CUSTOMIZATION.RESET_DEFAULTS') }}
      </button>
      <button
        class="rounded bg-n-brand px-4 py-2 text-sm font-semibold text-white"
        @click="save"
      >
        {{ t('PAINEL.STATUS_CUSTOMIZATION.SAVE') }}
      </button>
    </div>
  </div>
</template>
